export type ParsedSubmissionFieldKind = 'text' | 'email' | 'phone' | 'url';

export interface ParsedSubmissionField {
  label: string;
  value?: string;
  kind: ParsedSubmissionFieldKind;
  children?: ParsedSubmissionField[];
}

export interface ParsedSubmissionFieldsResult {
  fields: ParsedSubmissionField[];
  error?: string;
  rawText?: string;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const URL_RE = /^https?:\/\/\S+$/i;
const PHONE_RE = /^\+?[\d\s().-]{7,}$/;

export function parseSubmissionFields(input: unknown): ParsedSubmissionFieldsResult {
  const parsed = parseSubmissionInput(input);
  if (!parsed.ok) return { fields: [], error: parsed.error, rawText: parsed.rawText };

  const root = parsed.value;
  if (!root || typeof root !== 'object' || Array.isArray(root)) {
    return {
      fields: [],
      error: 'Submission JSON must be an object.',
      rawText: typeof input === 'string' ? input : JSON.stringify(input),
    };
  }

  const fields = Object.entries(root)
    .flatMap(([key, value]) => normalizeField(humanizeLabel(key), value))
    .filter((field) => field.value || (field.children && field.children.length > 0));

  return { fields };
}

function parseSubmissionInput(input: unknown):
  | { ok: true; value: unknown }
  | { ok: false; error: string; rawText?: string } {
  if (typeof input === 'string') {
    const rawText = input.trim();
    if (!rawText) return { ok: true, value: {} };
    try {
      return { ok: true, value: JSON.parse(rawText) };
    } catch (err) {
      return {
        ok: false,
        error: err instanceof Error ? err.message : 'Invalid JSON.',
        rawText,
      };
    }
  }

  if (input === undefined || input === null) return { ok: true, value: {} };
  return { ok: true, value: input };
}

function normalizeField(label: string, value: unknown): ParsedSubmissionField[] {
  if (isEmptyValue(value) || !label) return [];

  if (Array.isArray(value)) {
    const primitiveValues = value
      .filter((item) => !isEmptyValue(item))
      .map((item) => stringifyPrimitive(item))
      .filter((item): item is string => Boolean(item));

    if (primitiveValues.length === value.filter((item) => !isEmptyValue(item)).length) {
      return [
        {
          label,
          value: primitiveValues.join(', '),
          kind: detectKind(primitiveValues.join(', ')),
        },
      ];
    }

    const children = value.flatMap((item, index) =>
      normalizeField(`${label} ${index + 1}`, item),
    );
    return children.length ? [{ label, kind: 'text', children }] : [];
  }

  if (typeof value === 'object') {
    const record = value as Record<string, unknown>;
    const combined = combineObjectValue(label, record);
    if (combined) return [combined];

    const children = Object.entries(record)
      .flatMap(([key, childValue]) => normalizeField(humanizeLabel(key), childValue))
      .filter((field) => field.value || (field.children && field.children.length > 0));

    return children.length ? [{ label, kind: 'text', children }] : [];
  }

  const text = stringifyPrimitive(value);
  if (!text) return [];
  return [{ label, value: text, kind: detectKind(text) }];
}

function combineObjectValue(
  label: string,
  record: Record<string, unknown>,
): ParsedSubmissionField | undefined {
  const directValue =
    firstString(record.value) ??
    firstString(record.text) ??
    firstString(record.label) ??
    firstString(record.name);
  if (directValue) return { label, value: directValue, kind: detectKind(directValue) };

  const first = firstString(record.first) ?? firstString(record.first_name) ?? firstString(record.firstName);
  const last = firstString(record.last) ?? firstString(record.last_name) ?? firstString(record.lastName);
  if (first || last) {
    const value = [first, last].filter(Boolean).join(' ');
    return { label, value, kind: 'text' };
  }

  const addressParts = [
    firstString(record.street) ??
      firstString(record.street_address) ??
      firstString(record.address1) ??
      firstString(record.line1),
    firstString(record.street2) ??
      firstString(record.street_address_2) ??
      firstString(record.address2) ??
      firstString(record.line2),
    firstString(record.city),
    firstString(record.state) ?? firstString(record.province),
    firstString(record.zip) ?? firstString(record.postal_code) ?? firstString(record.postalCode),
    firstString(record.country),
  ].filter(Boolean);

  if (addressParts.length >= 2) {
    return { label, value: addressParts.join(', '), kind: 'text' };
  }

  return undefined;
}

function firstString(value: unknown): string | undefined {
  const text = stringifyPrimitive(value);
  return text || undefined;
}

function stringifyPrimitive(value: unknown): string | undefined {
  if (isEmptyValue(value)) return undefined;
  if (typeof value === 'string') return cleanText(value);
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  return undefined;
}

function cleanText(value: string): string {
  return value
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'")
    .replace(/\s+/g, ' ')
    .trim();
}

function detectKind(value: string): ParsedSubmissionFieldKind {
  if (EMAIL_RE.test(value)) return 'email';
  if (URL_RE.test(value)) return 'url';
  if (PHONE_RE.test(value) && value.replace(/\D/g, '').length >= 7) return 'phone';
  return 'text';
}

function isEmptyValue(value: unknown): boolean {
  if (value === undefined || value === null) return true;
  if (typeof value === 'string') return value.trim().length === 0;
  if (Array.isArray(value)) return value.every(isEmptyValue);
  if (typeof value === 'object') return Object.values(value as Record<string, unknown>).every(isEmptyValue);
  return false;
}

function humanizeLabel(key: string): string {
  return cleanText(key)
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\w\S*/g, (word) => word.charAt(0).toUpperCase() + word.slice(1));
}
